import { useRef, useState } from 'react'
import { Box, Typography, TextField, CircularProgress, List, ListItemButton, ListItemText, InputAdornment } from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import { loadOptionsForExistingPatient } from 'src/clinic/components/ClinicManageBookingsPage/ClinicManageBookingComponents'

/**
 * Patient selection for the header "+" flow (no appointment context). Reuses the existing patient
 * search helper used by the booking patient search, then maps the chosen option into the
 * patientContext the modal expects.
 */
export default function PatientPickerStep({ clinicId, clinicBrandId, onSelect }) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState([])
  const [searching, setSearching] = useState(false)
  const [searched, setSearched] = useState(false)
  const debounceRef = useRef(null)

  const runSearch = (q) => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    if (!q || q.trim().length < 2) {
      setResults([])
      setSearched(false)
      return
    }
    debounceRef.current = setTimeout(async () => {
      setSearching(true)
      try {
        let apiSignature = '/api/secure/mentor/patient/list'
        apiSignature = `${apiSignature}?pageSize=20`
        const data = await loadOptionsForExistingPatient(q, [], {
          page: 1,
          clinicId,
          clinicBrandId,
          mentorAuthorisedToSaveAndGetPatientsUsingIdb: false,
          patientSearchApiSignature: apiSignature,
          setAutoFillStuffForNewPatientString: () => {},
        })
        setResults((data?.options || []).filter((o) => o && o.id && !o.isAddRelativeOption))
      } catch (e) {
        setResults([])
      } finally {
        setSearching(false)
        setSearched(true)
      }
    }, 300)
  }

  const handleChange = (e) => {
    const v = e.target.value
    setQuery(v)
    runSearch(v)
  }

  const handleSelect = (option) => {
    onSelect({
      patientId: option.id,
      patientName: option.name || option.firstName || '',
      patientGender: option.gender || '',
      patientDob: option.dateOfBirth || option.dob || null,
    })
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Typography sx={{ fontSize: 14, fontWeight: 600 }}>Select a patient</Typography>
      <TextField
        autoFocus
        value={query}
        onChange={handleChange}
        placeholder="Search by name, mobile or email"
        size="small"
        fullWidth
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />
      {searching && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={22} />
        </Box>
      )}
      {!searching && searched && results.length === 0 && (
        <Typography sx={{ color: 'text.secondary', fontSize: 13, py: 2 }}>No patients found.</Typography>
      )}
      {!searching && results.length > 0 && (
        <List dense sx={{ maxHeight: 320, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          {results.map((option) => (
            <ListItemButton key={option.id} onClick={() => handleSelect(option)}>
              <ListItemText
                primary={option.name || option.firstName || 'Patient'}
                secondary={[option.gender, option.mobile || option.email].filter(Boolean).join(' • ') || undefined}
              />
            </ListItemButton>
          ))}
        </List>
      )}
    </Box>
  )
}
